import { extractErrorMessage, isError } from 'utils/ErrorMessages'

import agentsEpics from './containers/Agents/epics'
import agentsDashboardEpics from './containers/AgentsDashboard/epics'
import appEpics from './containers/App/epics'
import eulaModalEpic from './containers/EULAModal/epics'
import firstLoginModalepic from './containers/FirstLoginModal/epics'
import globalBannerEpics from './containers/GlobalBanner/epics'
import accountSetupEpics from './containers/Navigation/AccountSetup/epics'
import newReleaseEpics from './containers/NewRelease/epics'
import onboardingWizardEpics from './containers/OnboardingWizard/epics'
import partnerOptionsEpics from './containers/PartnerOptions/epics'
import pollingEpics from './containers/Polling/epics'
import reIntegrationEpics from './containers/Reintegration/epics'
import scenarioFormEpic from './containers/ScenarioForm/epics'
import scenarioPreviewEpic from './containers/ScenarioPreview/epics'
import { connectWiseEpics } from './containers/Settings/MalwareDetection/ConnectWise'
import { psaWizardEpics } from './containers/Settings/MalwareDetection/PSA'
import shareEpics from './containers/Share/epics'
import shareReportEpics from './containers/ShareReport/epics'
import spearPhishingAgentEpics from './containers/SpearPhishingAgent/epics'
import upgradeModalEpic from './containers/UpgradeLicenseModal/epics'
import socketLifecycleEpics from './services/socket/epic'
import { combineEpicsIsolated } from './utils/epicRegistry'

import * as Sentry from '@sentry/browser'

/**
 * Common epics requests error handler
 *
 * @param {function} func - the relevant action callback
 * @param {object} error - an action error
 * @param {array} expectedErrorCodes - an array of possible status codes, >= 500 are skipped
 */
export const epicsErrorHandler = (func, error, expectedErrorCodes = []) => {
  if (isError(error)) {
    // avoid catching unhandled errors by this function
    throw error
  } else {
    return func({
      title: 'Error',
      detail: extractErrorMessage(error),
      status: error.status,
      response: error.response,
      expectedErrorCodes,
    })
  }
}

// Named explicitly, rather than left implicit in the ALWAYS_ON_EPICS list
// below, so the two deltas from a plain per-container epics split are
// auditable: SpearPhishingAgent was promoted from route-scoped to always-on,
// and ConnectWise was extracted out of the Settings barrel into its own key.
export const PROMOTED_TO_ALWAYS_ON = ['SpearPhishingAgent']
export const EXTRACTED_FROM_BARREL = ['ConnectWise']

// The 21 ALWAYS-ON registry keys. Route-scoped keys live in
// src/routeEpics.js's ROUTE_EPICS instead. Each entry is
// `[key, epic]` pairs, injected once at store creation.
export const ALWAYS_ON_EPICS = [
  ['App', appEpics],
  ['Agents', agentsEpics],
  ['AgentsDashboard', agentsDashboardEpics],
  ['PartnerOptions', partnerOptionsEpics],
  ['Polling', pollingEpics],
  ['GlobalBanner', globalBannerEpics],
  ['NewRelease', newReleaseEpics],
  ['OnboardingWizard', onboardingWizardEpics],
  ['Reintegration', reIntegrationEpics],
  ['Share', shareEpics],
  ['ShareReport', shareReportEpics],
  ['UpgradeLicenseModal', upgradeModalEpic],
  ['ScenarioForm', scenarioFormEpic],
  ['ScenarioPreview', scenarioPreviewEpic],
  ['FirstLoginModal', firstLoginModalepic],
  ['EULAModal', eulaModalEpic],
  ['Navigation/AccountSetup', accountSetupEpics],
  // PROMOTED_TO_ALWAYS_ON: all 15 unguarded reads in
  // SpearPhishingAgent/selectors.js target the STATIC state.spearPhishingAgent
  // slice, which outlives every route; the route-scoped reducer entry never
  // mounts (it's inert), so promoting the key is a zero-behaviour-change fix
  // for a slice whose only real consumer already outlives the route.
  ['SpearPhishingAgent', spearPhishingAgentEpics],
  // EXTRACTED_FROM_BARREL: connectWise (reducer.js:48) is a static slice. If
  // it stayed nested inside the route-scoped Settings barrel, ejecting
  // Settings could strand a mid-flight fetch's loader forever. Extracted to
  // its own always-on key with a matching lifetime instead.
  ['ConnectWise', connectWiseEpics],
  // psaWizard is a static slice (reducer.js), same lifetime argument as ConnectWise.
  ['PsaWizard', psaWizardEpics],
  // The PLG notifications socket is app-lifetime by design (its debug probes
  // must not be tied to a page route). The module exports a plain epic array,
  // so it is wrapped into an isolated barrel here rather than at its export.
  ['Socket', combineEpicsIsolated(...socketLifecycleEpics)],
]
